import type { SchedulerRef } from '@lmt/unicorn-shared/app/common/devexpress/Scheduler/dx-scheduler';
import clsx from 'clsx';
import { addMilliseconds } from 'date-fns';
import { ResizeDirection } from 're-resizable';
import React, {
  FC,
  MouseEventHandler,
  RefObject,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DraggableData, DraggableEvent } from 'react-draggable';
import { ResizableDelta, Rnd } from 'react-rnd';
import { isSelectedIntervalAlreadyTaken } from '../visit/common/time-operations';

interface IntervalSelectionProps {
  interval?: Interval;
  schedulerRef: RefObject<SchedulerRef>;
  offsetDetector: (dateTime: Date | number) => number;
  timeDetector: (offset: number) => Date | undefined;
  cellWidth: number;
  cellDurationInMills: number;
  onIntervalChanged: (newInterval: Interval) => void;
  height: number | string;
  minDateTime?: Date;
  maxDateTime?: Date;
  takenTimeSlots: Interval[];
  stepsInCell: number;
}

import styles from './IntervalSelection.module.scss';
import { useEventCallback } from '@mui/material';

const leftMouseButtonKey = 0;

export const IntervalSelection: FC<IntervalSelectionProps> = React.memo(
  function IntervalSelection(props) {
    const {
      interval,
      offsetDetector,
      timeDetector,
      cellWidth,
      cellDurationInMills,
      onIntervalChanged,
      height,
      minDateTime,
      maxDateTime,
      takenTimeSlots,
      stepsInCell,
    } = props;

    const gridCellWidth = cellWidth / stepsInCell;

    const alignOffsetByGrid = useEventCallback((offsetX: number) => {
      const gridCellWidth = (cellWidth ?? 0) / stepsInCell;
      const unalignedOffset = offsetX % gridCellWidth;
      return (
        offsetX -
        unalignedOffset +
        Math.floor(unalignedOffset / gridCellWidth) * gridCellWidth
      );
    });

    const fitIntoTimeBounds = useEventCallback((dateTime: Date) => {
      const dateTimeInMills = dateTime.getTime();
      if (minDateTime && dateTimeInMills < minDateTime.getTime()) {
        return minDateTime;
      } else if (maxDateTime && dateTimeInMills > maxDateTime.getTime()) {
        return maxDateTime;
      } else {
        return dateTime;
      }
    });

    const [initialHoldPoint, setInitialHoldPoint] = useState<
      { offset: number; globalOffset: number } | undefined
    >();
    const [currentHoldPoint, setCurrentHoldPoint] = useState<
      number | undefined
    >();

    const onMouseDown: MouseEventHandler<HTMLDivElement> = useEventCallback(
      (e) => {
        if (e.button !== leftMouseButtonKey) {
          return;
        }

        const offset = alignOffsetByGrid(e.nativeEvent.offsetX);
        const globalOffset = alignOffsetByGrid(e.nativeEvent.clientX);
        const initialHoldPoint = { offset, globalOffset };
        setInitialHoldPoint(initialHoldPoint);

        let currentHoldPoint: number | undefined = undefined;

        const mouseMoveListener = (e2: MouseEvent) => {
          currentHoldPoint = alignOffsetByGrid(e2.clientX);
          setCurrentHoldPoint((previousHoldPoint) => {
            if (previousHoldPoint !== currentHoldPoint) {
              return currentHoldPoint;
            } else {
              return previousHoldPoint;
            }
          });
        };
        document.addEventListener('mousemove', mouseMoveListener);

        const onmouseupListener = () => {
          let startOffset: number;
          let endOffset: number;
          if (currentHoldPoint !== undefined) {
            const delta =
              alignOffsetByGrid(currentHoldPoint) -
              initialHoldPoint.globalOffset;
            if (delta >= 0) {
              startOffset = initialHoldPoint.offset;
              endOffset = initialHoldPoint.offset + delta;
            } else {
              startOffset = initialHoldPoint.offset + delta;
              if (startOffset < 0) {
                startOffset = 0;
              }
              endOffset = initialHoldPoint.offset;
            }
          } else {
            startOffset = initialHoldPoint.offset;
            if (interval) {
              endOffset =
                startOffset +
                (offsetDetector(interval.end) - offsetDetector(interval.start));
            } else {
              endOffset = startOffset;
            }
          }
          startOffset = alignOffsetByGrid(startOffset);
          endOffset = alignOffsetByGrid(endOffset);

          const startDateTime = fitIntoTimeBounds(timeDetector(startOffset)!);
          const endDateTime = fitIntoTimeBounds(timeDetector(endOffset)!);
          onIntervalChanged({ start: startDateTime, end: endDateTime });

          setInitialHoldPoint(undefined);
          setCurrentHoldPoint(undefined);
          document.removeEventListener('mousemove', mouseMoveListener);
          document.removeEventListener('mouseup', onmouseupListener);
        };
        document.addEventListener('mouseup', onmouseupListener);
      },
    );

    const selectionStarted = initialHoldPoint !== undefined;

    const containerStyle = useMemo(() => ({ height }), [height]);

    const renderSelectionShade = () => {
      if (initialHoldPoint === undefined) {
        return null;
      }

      const globalInitialPoint = initialHoldPoint!.globalOffset;

      let width: number;
      let offset: number;
      if (currentHoldPoint === undefined) {
        offset = initialHoldPoint.offset;
        if (interval) {
          width = offsetDetector(interval.end) - offsetDetector(interval.start);
        } else {
          width = 0;
        }
      } else if (globalInitialPoint <= currentHoldPoint) {
        width = currentHoldPoint - globalInitialPoint;
        offset = initialHoldPoint.offset;
      } else {
        const offsetDelta = globalInitialPoint - currentHoldPoint;
        offset = initialHoldPoint.offset - offsetDelta;
        if (offset < 0) {
          offset = 0;
        }
        width = initialHoldPoint.offset - offset;
      }

      const selectedInterval: Interval = {
        start: timeDetector(offset)!,
        end: timeDetector(offset + width)!,
      };

      return (
        <div
          style={{ left: offset, width, height, position: 'absolute' }}
          className={clsx(
            styles.intervalSelection,
            styles.resizableHandleLeft,
            styles.resizableHandleRight,
          )}
          data-error={isSelectedIntervalAlreadyTaken(
            selectedInterval,
            takenTimeSlots,
          )}
        />
      );
    };

    return (
      <div
        className={styles.container}
        style={containerStyle}
        onMouseDown={onMouseDown}
      >
        {interval && !selectionStarted && (
          <SelectedIntervalComponent
            interval={interval!}
            offsetDetector={offsetDetector}
            takenTimeSlots={takenTimeSlots}
            height={height}
            cellDurationInMills={cellDurationInMills}
            cellWidth={cellWidth}
            gridCellWidth={gridCellWidth}
            onIntervalChanged={onIntervalChanged}
            fitIntoTimeBounds={fitIntoTimeBounds}
            stepsInCell={stepsInCell}
          />
        )}
        {selectionStarted && renderSelectionShade()}
      </div>
    );
  },
);

function detectNewIntervalOnDrag(
  fitIntoTimeBounds: (dateTime: Date) => Date,
  interval: Interval,
  accumulatedDrag: number,
  cellWidth: number,
  cellDurationInMills: number,
  stepsInCell: number,
) {
  const newStart = fitIntoTimeBounds(
    addMilliseconds(
      interval.start,
      (Math.round((accumulatedDrag / cellWidth) * stepsInCell) / stepsInCell) *
        cellDurationInMills,
    ),
  );
  const newEnd = fitIntoTimeBounds(
    addMilliseconds(
      interval.end,
      (Math.round((accumulatedDrag / cellWidth) * stepsInCell) / stepsInCell) *
        cellDurationInMills,
    ),
  );
  return { start: newStart, end: newEnd };
}

function detectNewIntervalOnResize(
  interval: Interval,
  dir: ResizeDirection,
  fitIntoTimeBounds: (dateTime: Date) => Date,
  widthDelta: number,
  cellWidth: number,
  cellDurationInMills: number,
  stepsInCell: number,
) {
  let newStart = interval.start;
  let newEnd = interval.end;
  if (dir === 'left') {
    newStart = fitIntoTimeBounds(
      addMilliseconds(
        interval.start,
        -(Math.round((widthDelta / cellWidth) * stepsInCell) / stepsInCell) *
          cellDurationInMills,
      ),
    );
  } else if (dir === 'right') {
    newEnd = fitIntoTimeBounds(
      addMilliseconds(
        interval.end,
        (Math.round((widthDelta / cellWidth) * stepsInCell) / stepsInCell) *
          cellDurationInMills,
      ),
    );
  }
  return { start: newStart, end: newEnd };
}

const SelectedIntervalComponent: FC<{
  interval: Interval;
  offsetDetector: (dateTime: Date | number) => number;
  cellDurationInMills: number;
  height: number | string;
  cellWidth: number;
  gridCellWidth: number;
  fitIntoTimeBounds: (dateTime: Date) => Date;
  onIntervalChanged: (newInterval: Interval) => void;
  takenTimeSlots: Interval[];
  stepsInCell: number;
}> = React.memo(function SelectedIntervalComponent(props) {
  const {
    interval,
    offsetDetector,
    cellDurationInMills,
    height,
    cellWidth,
    gridCellWidth,
    fitIntoTimeBounds,
    onIntervalChanged,
    takenTimeSlots,
    stepsInCell,
  } = props;
  const start = interval.start;
  const end = interval.end;
  const startOffset = offsetDetector(start);
  const endOffset = offsetDetector(end);
  const delta = endOffset - startOffset;

  const size = useMemo(() => ({ height, width: delta }), [height, delta]);
  const position = useMemo(() => ({ x: startOffset, y: 0 }), [startOffset]);

  const currentIntervalIsAlreadyTaken = isSelectedIntervalAlreadyTaken(
    interval,
    takenTimeSlots,
  );
  const [newIntervalIsAlreadyTaken, setNewIntervalIsAlreadyTaken] = useState(
    currentIntervalIsAlreadyTaken,
  );
  const [intervalIsBeingModified, setIntervalIsBeingModified] = useState(false);

  const onResizeStart = useEventCallback(() => {
    setIntervalIsBeingModified(true);
    setNewIntervalIsAlreadyTaken(currentIntervalIsAlreadyTaken);
  });
  const lastResizeDelta = useRef(0);
  const onResize = useEventCallback(
    (
      _e: MouseEvent | TouchEvent,
      dir: ResizeDirection,
      _elementRef: HTMLElement,
      delta: ResizableDelta,
    ) => {
      const widthDelta = delta.width;
      if (lastResizeDelta.current !== widthDelta) {
        lastResizeDelta.current = widthDelta;
        const newInterval = detectNewIntervalOnResize(
          interval,
          dir,
          fitIntoTimeBounds,
          widthDelta,
          cellWidth,
          cellDurationInMills,
          stepsInCell,
        );
        setNewIntervalIsAlreadyTaken(
          isSelectedIntervalAlreadyTaken(newInterval, takenTimeSlots),
        );
      }
    },
  );

  const onResizeStop = useEventCallback(
    (
      _e: MouseEvent | TouchEvent,
      dir: ResizeDirection,
      _elementRef: HTMLElement,
      delta: ResizableDelta,
    ) => {
      const widthDelta = delta.width;
      if (widthDelta !== 0) {
        const newInterval = detectNewIntervalOnResize(
          interval,
          dir,
          fitIntoTimeBounds,
          widthDelta,
          cellWidth,
          cellDurationInMills,
          stepsInCell,
        );
        onIntervalChanged(newInterval);
      }
      lastResizeDelta.current = 0;
      setIntervalIsBeingModified(false);
    },
  );

  const onDragStart = useEventCallback(() => {
    setIntervalIsBeingModified(true);
    setNewIntervalIsAlreadyTaken(currentIntervalIsAlreadyTaken);
  });
  const accumulatedDrag = useRef(0);
  const onDrag = useEventCallback((_e: DraggableEvent, data: DraggableData) => {
    accumulatedDrag.current += data.deltaX;
    const newInterval = detectNewIntervalOnDrag(
      fitIntoTimeBounds,
      interval,
      accumulatedDrag.current,
      cellWidth,
      cellDurationInMills,
      stepsInCell,
    );
    setNewIntervalIsAlreadyTaken(
      isSelectedIntervalAlreadyTaken(newInterval, takenTimeSlots),
    );
  });

  const onDragStop = useEventCallback(() => {
    if (accumulatedDrag.current !== 0) {
      const newInterval = detectNewIntervalOnDrag(
        fitIntoTimeBounds,
        interval,
        accumulatedDrag.current,
        cellWidth,
        cellDurationInMills,
        stepsInCell,
      );
      onIntervalChanged(newInterval);
      accumulatedDrag.current = 0;
    }
    setIntervalIsBeingModified(false);
  });

  const onMouseDown = useEventCallback((e: MouseEvent) => {
    e.stopPropagation();
  });

  const resizingConfig = useMemo(() => ({ left: true, right: true }), []);
  const grid: [number, number] = useMemo(() => {
    return [gridCellWidth, 1];
  }, [gridCellWidth]);

  return (
    <Rnd
      className={clsx(
        styles.intervalSelection,
        styles.resizableHandleLeft,
        styles.resizableHandleRight,
      )}
      size={size}
      position={position}
      resizeGrid={grid}
      minWidth={0}
      enableResizing={resizingConfig}
      dragAxis={'x'}
      dragGrid={grid}
      onResizeStart={onResizeStart}
      onResize={onResize}
      onResizeStop={onResizeStop}
      onDragStart={onDragStart}
      onDrag={onDrag}
      onDragStop={onDragStop}
      onMouseDown={onMouseDown}
      bounds={'parent'}
      data-error={
        intervalIsBeingModified
          ? newIntervalIsAlreadyTaken
          : currentIntervalIsAlreadyTaken
      }
    />
  );
});
